import { useCallback, useEffect } from 'react';

import dayjs from 'dayjs';
import { motion, useAnimationControls } from 'framer-motion';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import diamondGem from '../../../../../../assets/bingo/diamond_gem.webp';
import diamondGlitter from '../../../../../../assets/bingo/diamond_widget_glitter.webp';
import Button from '../../../../../../components/buttons/Button';
import { useEventRotation } from '../../../../../../components/LobbyPassBanner/useEventRotation';
import { BINGO_DAUB, BINGO_WIDGET_TIMER_BUFFER_MS } from '../../../../constants';
import { useBingoAnimationReady, useBingoWidgetCallbacks } from '../../../../hooks';
import { BingoWidgetLogo } from '../../BingoWidgetLogo/BingoWidgetLogo';
import { BingoWidgetTimer } from '../../BingoWidgetTimer/BingoWidgetTimer';
import { BingoWideWidgetWrapper } from '../BingoWideWidgetWrapper/BingoWideWidgetWrapper';

import styles from './BingoUpcomingWideWidget.module.scss';

function BingoUpcomingWideWidgetComponent() {
  const { t } = useTranslation();
  const animationReadyRef = useBingoAnimationReady();
  const { onPurchaseClick } = useBingoWidgetCallbacks();

  const { shouldRotate, upcomingEvents, displayEvent } = useEventRotation();

  const topWrapperControls = useAnimationControls();
  const bottomWrapperControls = useAnimationControls();

  const rotationEnabled = shouldRotate && !!displayEvent;
  const currentEvent = rotationEnabled ? displayEvent : upcomingEvents[0];

  const slideTop = useCallback(async () => {
    await topWrapperControls.start({ y: -300, transition: { duration: 0.7 } });
    await topWrapperControls.start({ y: 0, opacity: 1, transition: { duration: 0.5 } });
  }, [topWrapperControls]);

  const slideBottom = useCallback(async () => {
    await bottomWrapperControls.start({ y: 300, transition: { duration: 0.7 } });
    await bottomWrapperControls.start({ y: 0, opacity: 1, transition: { duration: 0.5 } });
  }, [bottomWrapperControls]);

  const slide = useCallback(() => {
    void slideTop();
    void slideBottom();
  }, [slideBottom, slideTop]);

  useEffect(() => {
    if (!animationReadyRef.current) {
      return;
    }
    if (rotationEnabled) {
      slide();
    }
  }, [animationReadyRef, currentEvent?.id, rotationEnabled, slide]);

  if (!currentEvent) {
    return null;
  }

  const resolveFeature = () => {
    if (currentEvent.game.diamondLine) return 'diamond';
    if (currentEvent.game.isFreeDaubEvent) return 'daub';
    return 'general';
  };
  const feature = resolveFeature();
  const topWrapperExtended = feature !== 'general';
  const isFreeDaub = feature === 'daub';
  const isDiamond = feature === 'diamond';

  const bufferedStartMs = currentEvent?.startDate
    ? new Date(currentEvent.startDate).getTime() + BINGO_WIDGET_TIMER_BUFFER_MS
    : null;
  const timeToStartInMs = bufferedStartMs ? dayjs(bufferedStartMs).diff(dayjs(), 'milliseconds') : 0;
  const bingoType = currentEvent.coinType === 'GC' ? 'CASUAL' : 'SWEEP';

  return (
    <BingoWideWidgetWrapper feature={feature} onClick={onPurchaseClick}>
      <BingoWidgetLogo
        animationReadyRef={animationReadyRef}
        bingoType={bingoType}
        rotation={rotationEnabled}
        sizeType="wide"
      />
      <div className={styles.content}>
        <motion.div
          animate={topWrapperControls}
          className={topWrapperExtended ? styles.topWrapperBig : styles.topWrapper}
          initial={{ y: 0 }}
        >
          {isFreeDaub && (
            <div className={styles.freeDaubRow}>
              <span className={styles.freeDaubText}>{t('prime.bingo.eventFreeDaub.badge')}</span>
              <img alt="" className={styles.freeDaubImg} src={BINGO_DAUB} />
            </div>
          )}
          {isDiamond && (
            <div className={styles.diamondRow}>
              <div className={styles.diamondSparkles} style={{ backgroundImage: `url(${diamondGlitter})` }} />
              <img alt="" className={styles.diamondGem} src={diamondGem} />
              <span className={styles.diamondText}>{t('prime.diamondLine.badge')}</span>
              <img alt="" className={styles.diamondGem} src={diamondGem} />
            </div>
          )}
          <BingoWidgetTimer extended bingoType={bingoType} eventId={currentEvent.id} startsInMs={timeToStartInMs} />
        </motion.div>
        <motion.div
          animate={bottomWrapperControls}
          className={topWrapperExtended ? styles.btnWrapperSmall : styles.btnWrapper}
          initial={{ y: 0 }}
        >
          <Button className={topWrapperExtended ? styles.btnSmall : styles.btn}>
            {t('prime.lobbyBanner.purchase')}
          </Button>
        </motion.div>
      </div>
    </BingoWideWidgetWrapper>
  );
}

export const BingoUpcomingWideWidget = observer(BingoUpcomingWideWidgetComponent);
